import { ArrowLeft, Check, ChevronDown, Copy, Save, Search, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAttributes } from '../../../api/service.api';
import styles from '../../../assets/scss/pages/ServiceCreate.module.scss';
import KeypadInput from '../../../components/common/KeypadInput';
import api from '../../../config/axios-conf';
import { useToastContext } from '../../../contexts/ToastContext';

const LABEL = { dichvu: 'Dịch vụ', sanpham: 'Sản phẩm' };
const UNIT_SUGGESTIONS = ['Cái', 'Lượt', 'Chai', 'Kg', 'Tạ', 'Tấn', 'Hộp', 'Bộ', 'Lọ', 'Tuýp'];

// Ô nhập tiền: bấm vào bung bàn phím số ảo (dùng chuột), trả về chuỗi số thô.
function MoneyInput({ className, value, onChange, placeholder, title }) {
    return (
        <KeypadInput
            className={className}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            title={title || 'Nhập tiền'}
            mode="money"
            suffix="đ"
        />
    );
}

function emptyVariant(typeService) {
    return {
        sku: '',
        barcode: '',
        unit: '',
        importPrice: '',
        retailPrice: '',
        salePrice: '',
        capitalPrice: '',
        durationMinutes: '',
        quantity: '',
        notSell: '',
        alert: '',
        inStock: true,
        type: 'sell',
        attributeValueIds: [],
        _isProduct: typeService === 'sanpham',
    };
}

export default function ServiceCreateForm({ typeService }) {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const isProduct = typeService === 'sanpham';
    const label = LABEL[typeService] || 'Dịch vụ';

    // Form state
    const [form, setForm] = useState({
        name: '',
        description: '',
        displayLocation: '',
        internalNote: '',
        isWeb: false,
        urlImg: '',
    });
    const [variants, setVariants] = useState([emptyVariant(typeService)]);
    const [attributes, setAttributes] = useState([]);
    // selectedAttributes: { attrId: [valueId, ...] } — giống app
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [imgPreview, setImgPreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    const { showError } = useToastContext();
    // Báo lỗi đồng thời: banner inline (đầu form) + toast trượt góc phải.
    const reportError = (msg) => {
        setError(msg);
        showError(msg);
    };
    const [attrSearch, setAttrSearch] = useState('');
    const [showUnitSuggestions, setShowUnitSuggestions] = useState({});
    const [showSyncModal, setShowSyncModal] = useState(false);
    // Các trường được tích để đồng bộ — đơn vị mặc định tích
    const [syncFields, setSyncFields] = useState({
        unit: true,
        importPrice: false,
        retailPrice: false,
        salePrice: false,
        quantity: false,
        durationMinutes: false,
    });
    // Biến thể nguồn để lấy dữ liệu gốc đồng bộ
    const [syncSource, setSyncSource] = useState(0);
    const [showSourceDropdown, setShowSourceDropdown] = useState(false);
    const sourceDropdownRef = useRef(null);
    // Các biến thể đích được tích để đồng bộ — { idx: bool } cho idx khác nguồn
    const [syncTargets, setSyncTargets] = useState({});

    // Tổng số attribute values đã chọn
    const selectedAttrCount = Object.values(selectedAttributes).reduce((sum, arr) => sum + arr.length, 0);

    // Lấy tên attribute value theo id
    const getAttrValueName = useCallback(
        (valueId) => {
            for (const attr of attributes) {
                for (const val of attr.attributeValues || []) {
                    if (val.id === valueId) return val.value;
                }
            }
            return '';
        },
        [attributes],
    );

    // Tên hiển thị variant từ attributeValueIds
    const getVariantDisplayName = useCallback(
        (variant) => {
            if (!variant.attributeValueIds || variant.attributeValueIds.length === 0) {
                return 'Mặc định';
            }
            return variant.attributeValueIds.map((id) => getAttrValueName(id)).join(' - ');
        },
        [getAttrValueName],
    );

    // Cartesian product: generate variants từ selected attributes (giống app)
    const generateVariantsFromAttributes = useCallback(
        (selectedAttrs) => {
            // Lọc attributes có values được chọn
            const activeGroups = Object.entries(selectedAttrs)
                .filter(([, values]) => values.length > 0)
                .map(([, values]) => values);

            if (activeGroups.length === 0) {
                // Không chọn thuộc tính → 1 variant mặc định, preserve data variant[0]
                setVariants((prev) => {
                    const first = prev[0] || emptyVariant(typeService);
                    return [{ ...first, attributeValueIds: [] }];
                });
                return;
            }

            // Cartesian product
            let combinations = [[]];
            for (const group of activeGroups) {
                combinations = combinations.flatMap((combo) => group.map((valueId) => [...combo, valueId]));
            }

            setVariants((prev) => {
                return combinations.map((attrValueIds) => {
                    // Tìm variant cũ match (preserve data đã nhập)
                    const sorted = JSON.stringify([...attrValueIds].sort());
                    const existing = prev.find(
                        (old) => JSON.stringify([...(old.attributeValueIds || [])].sort()) === sorted,
                    );
                    if (existing) return { ...existing, attributeValueIds: attrValueIds };

                    // Variant mới — lấy default từ variant[0] nếu có
                    const base = prev[0] || {};
                    return {
                        ...emptyVariant(typeService),
                        unit: base.unit || '',
                        attributeValueIds: attrValueIds,
                    };
                });
            });
        },
        [typeService],
    );

    // Fetch attributes
    useEffect(() => {
        setLoadingData(true);
        getAttributes()
            .then(({ data }) => setAttributes(data.attributes || data || []))
            .catch(() => {})
            .finally(() => setLoadingData(false));
    }, []);

    // Đóng dropdown chọn nguồn khi click ra ngoài
    useEffect(() => {
        if (!showSourceDropdown) return;
        const handleClickOutside = (e) => {
            if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) {
                setShowSourceDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSourceDropdown]);

    const updateForm = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const updateVariant = (idx, key, value) => {
        setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, [key]: value } : v)));
    };

    const toggleSyncField = (key) => {
        setSyncFields((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // Tích tất cả biến thể đích trừ biến thể nguồn
    const selectAllTargetsExcept = (sourceIdx) => {
        const init = {};
        variants.forEach((_, i) => {
            if (i !== sourceIdx) init[i] = true;
        });
        setSyncTargets(init);
    };

    // Mở modal đồng bộ — mặc định nguồn là hàng đầu, tích tất cả biến thể đích
    const openSyncModal = () => {
        setSyncSource(0);
        selectAllTargetsExcept(0);
        setShowSourceDropdown(false);
        setShowSyncModal(true);
    };

    // Đổi biến thể nguồn — tích lại tất cả biến thể đích trừ nguồn mới
    const changeSyncSource = (idx) => {
        setSyncSource(idx);
        selectAllTargetsExcept(idx);
        setShowSourceDropdown(false);
    };

    const toggleSyncTarget = (idx) => {
        setSyncTargets((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    // Tất cả biến thể đích (idx khác nguồn) đã được tích chưa
    const allTargetsSelected =
        variants.length > 1 && variants.every((_, i) => i === syncSource || syncTargets[i]);

    const toggleAllTargets = () => {
        const next = !allTargetsSelected;
        const updated = {};
        variants.forEach((_, i) => {
            if (i !== syncSource) updated[i] = next;
        });
        setSyncTargets(updated);
    };

    // Sync variant từ biến thể nguồn — chỉ các trường + biến thể được tích
    const syncVariants = () => {
        if (variants.length <= 1) return;
        const source = variants[syncSource];
        if (!source) return;
        const keys = Object.keys(syncFields).filter((k) => syncFields[k]);
        setVariants((prev) =>
            prev.map((v, i) => {
                if (i === syncSource || !syncTargets[i]) return v;
                const updated = { ...v };
                keys.forEach((k) => {
                    updated[k] = source[k];
                });
                return updated;
            }),
        );
        setShowSyncModal(false);
    };

    // Toggle attribute value — giống app toggleAttributeValue()
    const toggleAttrValue = (attrId, valueId) => {
        setSelectedAttributes((prev) => {
            const current = prev[attrId] || [];
            const newValues = current.includes(valueId) ? current.filter((v) => v !== valueId) : [...current, valueId];
            const newSelected = { ...prev, [attrId]: newValues };

            // Regenerate variants ngay lập tức
            generateVariantsFromAttributes(newSelected);

            return newSelected;
        });
    };

    // Image upload
    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImgPreview(URL.createObjectURL(file));

        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post('/upload/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 20000,
            });
            updateForm('urlImg', data.url);
        } catch {
            reportError('Upload ảnh thất bại');
        }
    };

    // Submit
    const handleSubmit = async () => {
        setError('');

        if (!form.name.trim()) {
            reportError(`Vui lòng nhập tên ${label.toLowerCase()}`);
            return;
        }

        // Validate variants
        for (let i = 0; i < variants.length; i++) {
            const v = variants[i];
            if (!v.importPrice || Number(v.importPrice) <= 0) {
                reportError(`Biến thể ${i + 1}: Giá nhập phải lớn hơn 0`);
                return;
            }
            if (!v.retailPrice || Number(v.retailPrice) <= 0) {
                reportError(`Biến thể ${i + 1}: Giá bán phải lớn hơn 0`);
                return;
            }
            if (isProduct && Number(v.notSell || 0) > Number(v.quantity || 0)) {
                reportError(`Biến thể ${i + 1}: Số lượng không bán phải nhỏ hơn tồn kho`);
                return;
            }
        }

        setSaving(true);

        try {
            const body = {
                name: form.name.trim(),
                typeService,
                description: form.description,
                displayLocation: form.displayLocation,
                internalNote: form.internalNote,
                isWeb: form.isWeb,
                urlImg: form.urlImg,
                price: Number(variants[0].retailPrice) || 0,
                variants: variants.map((v) => ({
                    sku: v.sku,
                    barcode: v.barcode,
                    unit: v.unit,
                    importPrice: Number(v.importPrice) || 0,
                    retailPrice: Number(v.retailPrice) || 0,
                    salePrice: Number(v.salePrice) || 0,
                    capitalPrice: Number(v.capitalPrice) || 0,
                    inStock: v.inStock,
                    type: 'sell',
                    attributeValueIds: v.attributeValueIds || [],
                    ...(isProduct
                        ? {
                              quantity: Number(v.quantity) || 0,
                              notSell: Number(v.notSell) || 0,
                              alert: Number(v.alert) || 0,
                          }
                        : {
                              durationMinutes: v.durationMinutes ? Number(v.durationMinutes) : null,
                          }),
                    commissionTypeId: null,
                })),
            };

            await api.post('/services/create', body);
            setShowSuccess(true);
            setTimeout(() => navigate('/admin/services', { replace: true }), 1200);
        } catch (err) {
            reportError(err.response?.data?.message || `Tạo ${label.toLowerCase()} thất bại`);
        } finally {
            setSaving(false);
        }
    };

    if (loadingData) {
        return (
            <div className={styles.createForm}>
                <div className={styles.formHeader}>
                    <div className={styles.formHeaderLeft}>
                        <div className={styles.skeleton} style={{ width: 40, height: 40, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 180, height: 28, borderRadius: 8 }} />
                    </div>
                </div>
                <div className={styles.createLayout}>
                    <div className={styles.createLayoutLeft}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 160, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                {[1, 2, 3].map((i) => (
                                    <div key={i} style={{ marginBottom: 20 }}>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '30%', height: 16, borderRadius: 6, marginBottom: 8 }}
                                        />
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '100%', height: 44, borderRadius: 10 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.createLayoutRight}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 120, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                {[1, 2].map((i) => (
                                    <div key={i} style={{ marginBottom: 16 }}>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '40%', height: 14, borderRadius: 6, marginBottom: 8 }}
                                        />
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            {[1, 2, 3].map((j) => (
                                                <div
                                                    key={j}
                                                    className={styles.skeleton}
                                                    style={{ width: 70, height: 32, borderRadius: 16 }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.createForm}>
            {saving && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.spinner} />
                    Đang tạo {label.toLowerCase()}...
                </div>
            )}

            {/* Header */}
            <div className={styles.formHeader}>
                <div className={styles.formHeaderLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.formTitle}>Tạo {label}</h1>
                </div>
                <button className={styles.submitBtn} onClick={handleSubmit} disabled={saving}>
                    <Save /> Lưu {label.toLowerCase()}
                </button>
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            <div className={styles.createLayout}>
                {/* ===== CỘT TRÁI: Form chính ===== */}
                <div className={styles.createLayoutLeft}>
                    {/* Thông tin cơ bản: Tên + Ảnh */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin {label.toLowerCase()}</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formLabel}>
                                        Tên {label.toLowerCase()} <span>*</span>
                                    </label>
                                    <input
                                        className={styles.formInput}
                                        value={form.name}
                                        onChange={(e) => updateForm('name', e.target.value)}
                                        placeholder={`Nhập tên ${label.toLowerCase()}`}
                                    />
                                </div>

                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formLabel}>Ảnh {label.toLowerCase()}</label>
                                    <div className={styles.imageUpload}>
                                        <div className={styles.imagePreview} onClick={() => fileRef.current?.click()}>
                                            {imgPreview || form.urlImg ? (
                                                <img src={imgPreview || form.urlImg} alt="Preview" />
                                            ) : (
                                                <div className={styles.imageUploadPlaceholder}>
                                                    <Upload />
                                                    <span>Chọn ảnh</span>
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept="image/*"
                                            className={styles.imageUploadInput}
                                            onChange={handleImageSelect}
                                        />
                                        <button
                                            className={styles.imageUploadBtn}
                                            onClick={() => fileRef.current?.click()}
                                        >
                                            <Upload /> Tải ảnh lên
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Giá & thông số — hiện khi CHƯA chọn thuộc tính */}
                    {selectedAttrCount === 0 && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>Giá & thông số</div>
                            <div className={styles.cardBody}>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            Đơn vị
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className={styles.formInput}
                                                style={{ width: '100%' }}
                                                value={variants[0].unit}
                                                onChange={(e) => updateVariant(0, 'unit', e.target.value)}
                                                onFocus={() => setShowUnitSuggestions((p) => ({ ...p, 0: true }))}
                                                onBlur={() => setTimeout(() => setShowUnitSuggestions((p) => ({ ...p, 0: false })), 150)}
                                                placeholder="VD: Lượt, Chai, Cái"
                                            />
                                            {showUnitSuggestions[0] && UNIT_SUGGESTIONS.filter((u) => !variants[0].unit || u.toLowerCase().includes(variants[0].unit.toLowerCase())).length > 0 && (
                                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1.5px solid #ECEEF2', borderRadius: 10, boxShadow: '0 4px 8px rgba(0,0,0,0.06)', zIndex: 10, overflow: 'hidden' }}>
                                                    {UNIT_SUGGESTIONS.filter((u) => !variants[0].unit || u.toLowerCase().includes(variants[0].unit.toLowerCase())).map((u) => (
                                                        <button key={u} type="button" style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 15 }} onMouseDown={() => { updateVariant(0, 'unit', u); setShowUnitSuggestions((p) => ({ ...p, 0: false })); }}>{u}</button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            Giá nhập <span>*</span>
                                        </label>
                                        <MoneyInput
                                            className={styles.formInput}
                                            value={variants[0].importPrice}
                                            onChange={(val) => updateVariant(0, 'importPrice', val)}
                                            placeholder="0"
                                            title="Giá nhập"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            Giá bán <span>*</span>
                                        </label>
                                        <MoneyInput
                                            className={styles.formInput}
                                            value={variants[0].retailPrice}
                                            onChange={(val) => updateVariant(0, 'retailPrice', val)}
                                            placeholder="0"
                                            title="Giá bán"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Giá khuyến mãi</label>
                                        <MoneyInput
                                            className={styles.formInput}
                                            value={variants[0].salePrice}
                                            onChange={(val) => updateVariant(0, 'salePrice', val)}
                                            placeholder="0"
                                            title="Giá khuyến mãi"
                                        />
                                    </div>
                                    {!isProduct && (
                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Thời gian (phút)</label>
                                            <KeypadInput
                                                className={styles.formInput}
                                                mode="integer"
                                                value={variants[0].durationMinutes}
                                                onChange={(val) => updateVariant(0, 'durationMinutes', val)}
                                                placeholder="VD: 30"
                                                title="Thời gian (phút)"
                                                suffix="phút"
                                            />
                                        </div>
                                    )}
                                    {isProduct && (
                                        <>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Tồn kho</label>
                                                <KeypadInput
                                                    className={styles.formInput}
                                                    mode="integer"
                                                    value={variants[0].quantity}
                                                    onChange={(val) => updateVariant(0, 'quantity', val)}
                                                    placeholder="0"
                                                    title="Tồn kho"
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Không bán</label>
                                                <KeypadInput
                                                    className={styles.formInput}
                                                    mode="integer"
                                                    value={variants[0].notSell}
                                                    onChange={(val) => updateVariant(0, 'notSell', val)}
                                                    placeholder="0"
                                                    title="Không bán"
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Cảnh báo tồn</label>
                                                <KeypadInput
                                                    className={styles.formInput}
                                                    mode="integer"
                                                    value={variants[0].alert}
                                                    onChange={(val) => updateVariant(0, 'alert', val)}
                                                    placeholder="0"
                                                    title="Cảnh báo tồn"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Biến thể — chỉ hiện khi CÓ chọn thuộc tính */}
                    {selectedAttrCount > 0 && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>Biến thể & Giá</div>
                            <div className={styles.cardBody}>
                                <div className={styles.variantsHeader}>
                                    <span>{variants.length} biến thể được tạo tự động</span>
                                    {variants.length > 1 && (
                                        <button className={styles.syncBtn} onClick={openSyncModal}>
                                            <Copy /> Đồng bộ biến thể
                                        </button>
                                    )}
                                </div>

                                <div className={styles.variantTableWrap}>
                                    <table className={styles.variantTable}>
                                        <thead>
                                            <tr>
                                                <th>Biến thể</th>
                                                <th>Đơn vị *</th>
                                                <th>Giá nhập *</th>
                                                <th>Giá bán *</th>
                                                <th>Giá KM</th>
                                                {!isProduct && <th>Phút</th>}
                                                {isProduct && <th>Tồn kho</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {variants.map((v, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <span className={styles.variantName}>
                                                            {getVariantDisplayName(v)}
                                                        </span>
                                                    </td>
                                                    <td style={{ position: 'relative' }}>
                                                        <input
                                                            className={styles.variantInput}
                                                            value={v.unit}
                                                            onChange={(e) => updateVariant(idx, 'unit', e.target.value)}
                                                            onFocus={() => setShowUnitSuggestions((p) => ({ ...p, [idx]: true }))}
                                                            onBlur={() => setTimeout(() => setShowUnitSuggestions((p) => ({ ...p, [idx]: false })), 150)}
                                                            placeholder="Đơn vị"
                                                        />
                                                        {showUnitSuggestions[idx] && UNIT_SUGGESTIONS.filter((u) => !v.unit || u.toLowerCase().includes(v.unit.toLowerCase())).length > 0 && (
                                                            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1.5px solid #ECEEF2', borderRadius: 10, boxShadow: '0 4px 8px rgba(0,0,0,0.06)', zIndex: 10, overflow: 'hidden' }}>
                                                                {UNIT_SUGGESTIONS.filter((u) => !v.unit || u.toLowerCase().includes(v.unit.toLowerCase())).map((u) => (
                                                                    <button key={u} type="button" style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 15 }} onMouseDown={() => { updateVariant(idx, 'unit', u); setShowUnitSuggestions((p) => ({ ...p, [idx]: false })); }}>{u}</button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <MoneyInput
                                                            className={styles.variantInput}
                                                            value={v.importPrice}
                                                            onChange={(val) => updateVariant(idx, 'importPrice', val)}
                                                            placeholder="0"
                                                            title="Giá nhập"
                                                        />
                                                    </td>
                                                    <td>
                                                        <MoneyInput
                                                            className={styles.variantInput}
                                                            value={v.retailPrice}
                                                            onChange={(val) => updateVariant(idx, 'retailPrice', val)}
                                                            placeholder="0"
                                                            title="Giá bán"
                                                        />
                                                    </td>
                                                    <td>
                                                        <MoneyInput
                                                            className={styles.variantInput}
                                                            value={v.salePrice}
                                                            onChange={(val) => updateVariant(idx, 'salePrice', val)}
                                                            placeholder="0"
                                                            title="Giá khuyến mãi"
                                                        />
                                                    </td>
                                                    {!isProduct && (
                                                        <td>
                                                            <KeypadInput
                                                                className={styles.variantInput}
                                                                mode="integer"
                                                                value={v.durationMinutes}
                                                                onChange={(val) =>
                                                                    updateVariant(idx, 'durationMinutes', val)
                                                                }
                                                                placeholder="0"
                                                                title="Thời gian (phút)"
                                                                suffix="phút"
                                                            />
                                                        </td>
                                                    )}
                                                    {isProduct && (
                                                        <td>
                                                            <KeypadInput
                                                                className={styles.variantInput}
                                                                mode="integer"
                                                                value={v.quantity}
                                                                onChange={(val) =>
                                                                    updateVariant(idx, 'quantity', val)
                                                                }
                                                                placeholder="0"
                                                                title="Tồn kho"
                                                            />
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Thông tin bổ sung: Mô tả, Vị trí, Ghi chú, Website */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin bổ sung</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formLabel}>Mô tả</label>
                                    <textarea
                                        className={styles.formTextarea}
                                        value={form.description}
                                        onChange={(e) => updateForm('description', e.target.value)}
                                        placeholder="Nhập mô tả"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Vị trí trưng bày</label>
                                    <input
                                        className={styles.formInput}
                                        value={form.displayLocation}
                                        onChange={(e) => updateForm('displayLocation', e.target.value)}
                                        placeholder="VD: Kệ 2, Bàn 3"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Ghi chú nội bộ</label>
                                    <input
                                        className={styles.formInput}
                                        value={form.internalNote}
                                        onChange={(e) => updateForm('internalNote', e.target.value)}
                                        placeholder="Ghi chú cho nhân viên"
                                    />
                                </div>
                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formToggle}>
                                        <input
                                            type="checkbox"
                                            checked={form.isWeb}
                                            onChange={(e) => updateForm('isWeb', e.target.checked)}
                                        />
                                        Hiển thị trên website
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* end createLayoutLeft */}

                {/* ===== CỘT PHẢI: Thuộc tính sidebar ===== */}
                <div className={styles.createLayoutRight}>
                    <div className={styles.attrSidebar}>
                        <div className={styles.attrSidebarHeader}>
                            <h3 className={styles.attrSidebarTitle}>Thuộc tính</h3>
                            {selectedAttrCount > 0 && (
                                <span className={styles.attrSidebarCount}>{selectedAttrCount} đã chọn</span>
                            )}
                        </div>

                        {/* Search */}
                        <div className={styles.attrSidebarSearch}>
                            <Search size={16} />
                            <input
                                value={attrSearch}
                                onChange={(e) => setAttrSearch(e.target.value)}
                                placeholder="Tìm thuộc tính..."
                            />
                            {attrSearch && (
                                <button onClick={() => setAttrSearch('')}>
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {attributes.length === 0 ? (
                            <div className={styles.attrSidebarEmpty}>Chưa có thuộc tính nào</div>
                        ) : (
                            (() => {
                                const keyword = attrSearch
                                    .trim()
                                    .toLowerCase()
                                    .replace(/\s/g, '')
                                    .normalize('NFD')
                                    .replace(/[\u0300-\u036f]/g, '');
                                const filtered = keyword
                                    ? attributes
                                          .map((attr) => {
                                              const attrNameMatch = attr.name
                                                  .toLowerCase()
                                                  .replace(/\s/g, '')
                                                  .normalize('NFD')
                                                  .replace(/[\u0300-\u036f]/g, '')
                                                  .includes(keyword);
                                              const matchedValues = (attr.attributeValues || []).filter((val) =>
                                                  val.value
                                                      .toLowerCase()
                                                      .replace(/\s/g, '')
                                                      .normalize('NFD')
                                                      .replace(/[\u0300-\u036f]/g, '')
                                                      .includes(keyword),
                                              );
                                              if (attrNameMatch) return attr;
                                              if (matchedValues.length > 0)
                                                  return { ...attr, attributeValues: matchedValues };
                                              return null;
                                          })
                                          .filter(Boolean)
                                    : attributes;

                                if (filtered.length === 0) {
                                    return <div className={styles.attrSidebarEmpty}>Không tìm thấy</div>;
                                }

                                return (
                                    <div className={styles.attrSidebarBody}>
                                        {filtered.map((attr) => (
                                            <div key={attr.id} className={styles.attrSection}>
                                                <div className={styles.attrTitle}>{attr.name}</div>
                                                <div className={styles.attrChips}>
                                                    {(attr.attributeValues || []).map((val) => (
                                                        <button
                                                            key={val.id}
                                                            className={`${styles.attrChip}${(selectedAttributes[attr.id] || []).includes(val.id) ? ` ${styles.attrChipActive}` : ''}`}
                                                            onClick={() => toggleAttrValue(attr.id, val.id)}
                                                        >
                                                            {val.value}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Preview variants đã generate */}
                                        {selectedAttrCount > 0 && (
                                            <div className={styles.attrSection}>
                                                <div className={styles.attrTitle}>
                                                    Biến thể sẽ tạo ({variants.length})
                                                </div>
                                                <div className={styles.attrChips}>
                                                    {variants.map((v, i) => (
                                                        <span
                                                            key={i}
                                                            className={`${styles.attrChip} ${styles.attrChipActive}`}
                                                        >
                                                            {getVariantDisplayName(v)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()
                        )}

                        {selectedAttrCount > 0 && (
                            <button
                                className={styles.attrSidebarClear}
                                onClick={() => {
                                    setSelectedAttributes({});
                                    generateVariantsFromAttributes({});
                                }}
                            >
                                Bỏ chọn tất cả
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* end createLayout */}

            {/* Thanh action cuối form */}
            <div className={styles.formFooter}>
                <button className={styles.cancelBtn} onClick={() => navigate(-1)} disabled={saving}>
                    Hủy
                </button>
                <button className={styles.footerSubmitBtn} onClick={handleSubmit} disabled={saving}>
                    <Save /> Lưu {label.toLowerCase()}
                </button>
            </div>

            {showSyncModal && (
                <div className={styles.syncOverlay} onClick={() => setShowSyncModal(false)}>
                    <div className={styles.syncModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.syncModalHeader}>
                            <span>Đồng bộ biến thể</span>
                            <button className={styles.syncModalClose} onClick={() => setShowSyncModal(false)}>
                                <X />
                            </button>
                        </div>
                        <p className={styles.syncModalDesc}>
                            Sao chép dữ liệu từ biến thể nguồn
                            <strong> «{getVariantDisplayName(variants[syncSource]) || ''}» </strong>
                            sang các biến thể được chọn.
                        </p>

                        <div className={styles.syncSourceRow}>
                            <span className={styles.syncSectionLabel}>Lấy dữ liệu gốc từ</span>
                            <div className={styles.syncSourceDropdown} ref={sourceDropdownRef}>
                                <button
                                    type="button"
                                    className={`${styles.syncSourceTrigger} ${showSourceDropdown ? styles.syncSourceTriggerOpen : ''}`}
                                    onClick={() => setShowSourceDropdown((o) => !o)}
                                >
                                    <span>{getVariantDisplayName(variants[syncSource]) || ''}</span>
                                    <ChevronDown />
                                </button>
                                {showSourceDropdown && (
                                    <div className={styles.syncSourceMenu}>
                                        {variants.map((v, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                className={`${styles.syncSourceOption} ${idx === syncSource ? styles.syncSourceOptionActive : ''}`}
                                                onClick={() => changeSyncSource(idx)}
                                            >
                                                <span>{getVariantDisplayName(v)}</span>
                                                {idx === syncSource && <Check />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.syncColumns}>
                        <div className={`${styles.syncSection} ${styles.syncColLeft}`}>
                            <div className={styles.syncSectionLabel}>Thông tin cần đồng bộ</div>
                            <div className={styles.syncChips}>
                                {[
                                    { key: 'unit', label: 'Đơn vị' },
                                    { key: 'importPrice', label: 'Giá nhập' },
                                    { key: 'retailPrice', label: 'Giá bán' },
                                    { key: 'salePrice', label: 'Giá KM' },
                                    ...(isProduct
                                        ? [{ key: 'quantity', label: 'Tồn kho' }]
                                        : [{ key: 'durationMinutes', label: 'Phút' }]),
                                ].map((opt) => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        className={`${styles.syncChip} ${syncFields[opt.key] ? styles.syncChipActive : ''}`}
                                        onClick={() => toggleSyncField(opt.key)}
                                    >
                                        {syncFields[opt.key] && <Check />}
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={`${styles.syncSection} ${styles.syncColRight}`}>
                            <div className={styles.syncSectionHead}>
                                <span className={styles.syncSectionLabel}>Áp dụng cho biến thể</span>
                                <button type="button" className={styles.syncSelectAll} onClick={toggleAllTargets}>
                                    {allTargetsSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                                </button>
                            </div>
                            <div className={styles.syncTargetList}>
                                {variants.map((v, idx) =>
                                    idx === syncSource ? null : (
                                        <label key={idx} className={styles.syncTargetItem}>
                                            <input
                                                type="checkbox"
                                                checked={!!syncTargets[idx]}
                                                onChange={() => toggleSyncTarget(idx)}
                                            />
                                            <span>{getVariantDisplayName(v)}</span>
                                        </label>
                                    ),
                                )}
                            </div>
                        </div>
                        </div>

                        <div className={styles.syncModalFooter}>
                            <button className={styles.syncCancelBtn} onClick={() => setShowSyncModal(false)}>
                                Huỷ
                            </button>
                            <button
                                className={styles.syncConfirmBtn}
                                onClick={syncVariants}
                                disabled={
                                    !Object.values(syncFields).some(Boolean) ||
                                    !Object.values(syncTargets).some(Boolean)
                                }
                            >
                                Đồng bộ {Object.values(syncTargets).filter(Boolean).length} biến thể
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>Tạo {label.toLowerCase()} thành công</span>
                    </div>
                </div>
            )}
        </div>
    );
}
